import React, { Suspense } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Briefcase } from 'lucide-react';
import { companyInfo, trustLogos } from '../data/mock';
import { useTheme } from '../context/ThemeContext';

const Spline = React.lazy(() => import('@splinetool/react-spline'));

const HeroSection = () => {
  const { theme } = useTheme();

  return (
    <section id="hero" className={`relative min-h-[90vh] overflow-hidden transition-colors duration-300 ${
      theme === 'dark' ? 'bg-black' : 'bg-gradient-to-br from-gray-50 to-gray-100'
    }`}>
      {/* Grid Pattern Overlay */}
      <div 
        className={`absolute inset-0 ${theme === 'dark' ? 'opacity-[0.06]' : 'opacity-[0.03]'}`}
        style={{
          backgroundImage: `
            repeating-linear-gradient(0deg, transparent, transparent 1px, transparent 1px, transparent calc(100% / 13)),
            repeating-linear-gradient(-90deg, ${theme === 'dark' ? '#fff' : '#000'}, ${theme === 'dark' ? '#fff' : '#000'} 1px, transparent 1px, transparent calc(100% / 13))
          `,
          backgroundSize: '100% 100%'
        }}
      />

      <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-[7.6923%] pt-12 sm:pt-16 lg:pt-24 pb-12 sm:pb-16 lg:pb-20">
        <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-8">
          {/* Left Content */}
          <div className="flex-1 z-10 text-center lg:text-left">
            {/* Badge */}
            <div className={`inline-flex items-center gap-2 px-3 sm:px-4 py-2 mb-6 sm:mb-8 border ${
              theme === 'dark' ? 'border-white/20 bg-white/5' : 'border-gray-300 bg-white'
            }`}>
              <span className="w-2 h-2 bg-[#00FFD1] rounded-full animate-pulse" />
              <span className={`text-xs sm:text-sm font-medium tracking-wide ${
                theme === 'dark' ? 'text-white/70' : 'text-gray-600'
              }`}>
                Now Accepting New Clients
              </span>
            </div>

            {/* Headline */}
            <h1 className={`text-3xl sm:text-4xl md:text-5xl lg:text-[60px] font-bold leading-[1.1] tracking-tight mb-4 sm:mb-6 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              {companyInfo.name}
            </h1>
            
            <p className={`text-lg sm:text-xl lg:text-2xl font-medium mb-4 sm:mb-6 ${
              theme === 'dark' ? 'text-[#00FFD1]' : 'text-[#00997A]'
            }`}>
              {companyInfo.tagline}
            </p>

            <p className={`text-base sm:text-lg lg:text-xl leading-relaxed mb-8 sm:mb-10 max-w-[540px] mx-auto lg:mx-0 ${
              theme === 'dark' ? 'text-white/80' : 'text-gray-600'
            }`}>
              {companyInfo.slogan}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start">
              <Link
                to="/contact"
                className={`group flex items-center justify-center gap-2 sm:gap-3 px-5 sm:px-6 py-3 sm:py-4 font-medium text-base sm:text-lg transition-all duration-300 ${
                  theme === 'dark'
                    ? 'bg-[#00FFD1] text-black hover:bg-[#00FFD1]/80'
                    : 'bg-[#00997A] text-white hover:bg-[#007A61]'
                }`}
              >
                <Briefcase size={18} className="sm:w-5 sm:h-5" />
                Request a Consultation
                <ArrowRight size={18} className="sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
              </Link>

              <Link
                to="/careers"
                className={`group flex items-center justify-center gap-2 sm:gap-3 px-5 sm:px-6 py-3 sm:py-4 font-medium text-base sm:text-lg transition-all duration-300 ${
                  theme === 'dark'
                    ? 'bg-white/10 text-white hover:bg-white/20 border border-white/20'
                    : 'bg-white text-gray-800 hover:bg-gray-50 border border-gray-300'
                }`}
              >
                <Users size={18} className="sm:w-5 sm:h-5" />
                Join Talent Network
              </Link>
            </div>

            {/* Trust Bar */}
            <div className={`mt-10 sm:mt-12 pt-8 sm:pt-10 border-t ${
              theme === 'dark' ? 'border-white/10' : 'border-gray-200'
            }`}>
              <p className={`text-xs sm:text-sm font-medium mb-3 sm:mb-4 ${
                theme === 'dark' ? 'text-white/50' : 'text-gray-500'
              }`}>
                EXPERIENCE SUPPORTING
              </p>
              <div className="flex flex-wrap gap-2 sm:gap-3 justify-center lg:justify-start">
                {trustLogos.map((logo, index) => (
                  <span 
                    key={index}
                    className={`px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm ${
                      theme === 'dark' 
                        ? 'bg-white/5 text-white/60 border border-white/10' 
                        : 'bg-gray-100 text-gray-600 border border-gray-200'
                    }`}
                  >
                    {logo}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Right Side - Spline 3D */}
          <div className="flex-1 relative w-full lg:w-auto min-h-[300px] sm:min-h-[400px] lg:min-h-[500px] hidden md:block">
            <div 
              className={`absolute inset-0 lg:right-[-50px] overflow-visible transition-all duration-500 ${
                theme === 'light' ? 'filter invert hue-rotate-180 brightness-95' : ''
              }`}
            >
              <Suspense fallback={
                <div className="w-full h-full flex items-center justify-center">
                  <div className={`w-12 h-12 sm:w-16 sm:h-16 border-2 border-t-transparent rounded-full animate-spin ${
                    theme === 'dark' ? 'border-[#00FFD1]' : 'border-[#00997A]'
                  }`} />
                </div>
              }>
                <Spline 
                  scene="https://prod.spline.design/NbVmy6DPLhY-5Lvg/scene.splinecode" 
                  style={{ width: '100%', height: '100%' }}
                />
              </Suspense>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Gradient */}
      <div className={`absolute bottom-0 left-0 right-0 h-24 sm:h-32 ${
        theme === 'dark' 
          ? 'bg-gradient-to-t from-black to-transparent' 
          : 'bg-gradient-to-t from-gray-50 to-transparent'
      }`} />
    </section>
  );
};

export default HeroSection;
